'use client';

import { useBunja } from 'bunja/react';
import {
  ComponentProps,
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  cloneElement,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useMount } from 'react-use';

import { OverlayProps, overlayBunja } from '@flipster/overlay';
import deferred, { Deferred } from '@flipster/utils/deferred';
import { EventEmitter } from '@flipster/utils/event-emitter';
import { generateID } from '@flipster/utils/identity';

import { Dialog } from './';

type DialogButtonElement = ReactElement<ComponentProps<typeof Dialog.Button>>;

interface SimpleDialogOptions {
  title: { label: ReactNode; leftAddon?: ReactNode };
  content: ReactNode;
  buttons: {
    direction?: 'horizontal' | 'vertical';
    primary: DialogButtonElement;
    secondary?: DialogButtonElement;
  };
}

interface DialogComponentProps<T> {
  isOpen: boolean;
  promise: Deferred<T>;
  unmount: () => void;
}

// 커스텀 옵션: ReactElement를 반환
type CustomDialogOptions<T = unknown> = (
  props: DialogComponentProps<T>,
) => ReactElement<ComponentProps<typeof Dialog.Root>>;
// 모든 옵션 타입을 통합
type DialogOpenOptions<T = unknown> = SimpleDialogOptions | CustomDialogOptions<T>;

type InternalDialogOptions<T = unknown> = {
  id: string;
  controlledClose: boolean;
  promise: Deferred<T>;
} & (Partial<SimpleDialogOptions> | { dialogComponent: CustomDialogOptions<T> });

const dialogEmitter = new EventEmitter<{ open: InternalDialogOptions; close: null }>();

export const DialogLayout = ({ children }: PropsWithChildren) => {
  const lastDialog = useRef<Deferred<unknown>>(undefined);
  const overlay = useBunja(overlayBunja);

  const handleDialog = useCallback(
    (options: InternalDialogOptions) => {
      if (lastDialog.current && lastDialog.current.state === 'pending') return;
      lastDialog.current = options.promise;
      const overlayId = overlay.open(
        ({ isOpen, close, unmount }) => <DialogManager isOpen={isOpen} close={close} unmount={unmount} {...options} />,
        { slot: 'alert' },
      );

      // ESC나 backdrop 클릭으로 overlay가 닫히면 Promise도 reject 처리
      void overlay.getClosed(overlayId).then(() => {
        if (options.controlledClose && options.promise.state === 'pending') {
          options.promise.reject(new Error('Dialog closed without action'));
        }
        lastDialog.current = undefined;
      });
    },
    [overlay],
  );

  const handleClose = useCallback(() => {
    overlay.closeSlot('alert');
    lastDialog.current = undefined;
  }, [overlay]);

  useEffect(() => {
    dialogEmitter.on('open', handleDialog);
    dialogEmitter.on('close', handleClose);
    return () => {
      dialogEmitter.off('open', handleDialog);
      dialogEmitter.off('close', handleClose);
    };
  }, [handleDialog, handleClose]);

  return children;
};

const DialogManager = ({
  isOpen,
  close,
  unmount,
  ...options
}: InternalDialogOptions & Pick<OverlayProps, 'isOpen' | 'close' | 'unmount'>) => {
  useMount(() => {
    options.promise.finally(close).catch(() => {});
  });

  if ('dialogComponent' in options) {
    const Component = options.dialogComponent;
    return <Component isOpen={isOpen} promise={options.promise} unmount={unmount} />;
  }
  return <DialogComponent isOpen={isOpen} {...options} />;
};

const DialogComponent = (options: Extract<InternalDialogOptions, { title?: unknown }> & { isOpen: boolean }) => {
  const { isOpen, title, content, buttons, controlledClose, promise } = options;

  const handleClose = () => {
    if (!controlledClose) {
      promise.resolve(undefined);
    }
  };

  const enhanceButton = (button?: DialogButtonElement) => {
    if (!button) return null;

    return cloneElement(button, {
      ...button.props,
      onClick: (e: MouseEvent<HTMLElement>) => {
        button.props.onClick?.(e as never);
        handleClose();
      },
    });
  };

  return (
    <Dialog.Root isOpen={isOpen}>
      {title?.label && <Dialog.Title leftAddon={title.leftAddon} label={title.label} />}
      {content && <Dialog.Content>{content}</Dialog.Content>}
      <Dialog.Footer direction={buttons?.direction}>
        {enhanceButton(buttons?.secondary)}
        {enhanceButton(buttons?.primary)}
      </Dialog.Footer>
    </Dialog.Root>
  );
};

function open<T>(options: SimpleDialogOptions): Promise<T>;
function open<T>(options: CustomDialogOptions<T>): Promise<T>;
function open<T>(options: DialogOpenOptions<T>): Promise<T> {
  const id = generateID('dialog-');
  const promise = deferred<T>();

  if (typeof options === 'function') {
    dialogEmitter.emit('open', {
      id,
      promise,
      controlledClose: true,
      dialogComponent: options as CustomDialogOptions,
    });
  } else {
    dialogEmitter.emit('open', {
      id,
      promise,
      controlledClose: false,
      ...options,
    });
  }

  return promise;
}

export const dialog = {
  open,
  close: () => {
    dialogEmitter.emit('close', null);
  },
};
